import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
// Quitamos import estático pesado:
// import { GoogleGenerativeAI } from "@google/generative-ai";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ContextGeneral from "../../servicios/contexto";

/** ===== Portal ===== */
function Portal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

/** ===== API ===== */
const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || "/api/chat";
const MAX_INPUT_CHARS = 400;
const TYR_SITE = "https://www.tyr-ai.com.ar";
const TYR_REDIRECT_HOSTS = ["tyr-ai.com.ar", "www.tyr-ai.com.ar"];

const SUGGESTIONS = [
  "¿En qué está trabajando Sebas actualmente?",
  "¿Qué es TYR?",
  "¿Qué tecnologias domina Sebas?",
  "Mostrame proyectos con Next.js",
  "¿Dónde vive y cómo lo contacto?",
];

/* === Hook mobile === */
function useIsMobile() {
  const [m, setM] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setM(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return m;
}

/** ===== Limpia etiquetas si el modelo se las cuela ===== */
function stripSectionLabels(txt) {
  const labels = [
  "Perfil",
  "Tecnologías",
  "Proyectos",
  "Contacto",
  "Educación",
  "Experiencia",
];
  let out = txt.replace(
    new RegExp(`^\\s*(?:${labels.join("|")})\\s*:\\s*`, "gmi"),
    ""
  );
  out = out.replace(/\n{3,}/g, "\n\n").trim();
  return out;
}

/** ===== Elimina puntuación pegada al final de URLs ===== */
function removeTerminalDotAfterUrls(txt) {
  // Quita .,;:!? inmediatamente después de una URL al final de palabra
  return txt.replace(/(https?:\/\/\S+?)([.,;:!?])(?=\s|$)/g, "$1");
}

/** ===== Dominios internos (mismo enrutado que Home) ===== */
const INTERNAL_HOSTS = ["sebassotelo.com.ar", "www.sebassotelo.com.ar"];

/** ===== Aliases internos -> rutas reales de la SPA ===== */
const PATH_ALIASES = {
  "/": "/",
  "/freelance": "/tyr",
  "/freelance/": "/tyr",
  "/sobre-mi": "/sobre-mi",
  "/sobre-mi/": "/sobre-mi",
  "/portafolio": "/sobre-mi",
  "/portafolio/": "/sobre-mi",
  "/portfolio": "/sobre-mi",
  "/portfolio/": "/sobre-mi",
  "/videos": "/videos",
};

/** Normaliza rutas internas: recorta slashes, aplica alias y fuerza /sobre-mi para #portfolio */
function normalizeInternalPath(pathWithSearchAndHash) {
  try {
    const m = pathWithSearchAndHash.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
    let rawPath = m?.[1] || "" || "/";
    const search = m?.[2] || "";
    const hash = m?.[3] || "";

    if (hash.toLowerCase() === "#portfolio") return "/sobre-mi";

    rawPath = rawPath.replace(/\/{2,}/g, "/");
    if (rawPath.length > 1 && rawPath.endsWith("/"))
      rawPath = rawPath.slice(0, -1);

    const aliased = PATH_ALIASES[rawPath] ?? rawPath;
    return `${aliased}${search}`;
  } catch {
    return "/sobre-mi";
  }
}

/** ===== Autolink: internas => navigate("/ruta"); externas => _blank ===== */
function renderWithLinks(text, navigate) {
  const regex = /(https?:\/\/[^\s)]+)|((?<!\w)\/[A-Za-z0-9\-/_#?=.%]+)/g;
  const parts = [];
  let last = 0;
  let m;

  const go = (e, toString) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const clean = toString.replace(/([^:]\/)\/+/g, "$1");
    const normalized = normalizeInternalPath(clean);

    if (normalized === "/tyr") {
      window.location.assign(TYR_SITE);
      return;
    }

    navigate(normalized);
  };

  while ((m = regex.exec(text)) !== null) {
    const [match, absoluteUrl] = m;
    const start = m.index;
    const end = start + match.length;
    if (start > last) parts.push(text.slice(last, start));

    if (absoluteUrl) {
      let handledAsInternal = false;
      let handleAsTyrSite = false;
      let to = match;
      try {
        const base =
          typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost";
        const u = new URL(match, base);
        const sameOrigin =
          typeof window !== "undefined" && u.origin === window.location.origin;
        const knownInternal = INTERNAL_HOSTS.includes(u.hostname);
        const tyrHost = TYR_REDIRECT_HOSTS.includes(u.hostname);

        if (tyrHost) {
          handleAsTyrSite = true;
          to = TYR_SITE;
        } else if (sameOrigin || knownInternal) {
          handledAsInternal = true;
          to = `${u.pathname}${u.search}${u.hash}`;
        }
      } catch {
        /* externo */
      }

      if (handleAsTyrSite) {
        parts.push(
          <button
            key={`tyr-${start}`}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.assign(TYR_SITE);
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="underline underline-offset-2 decoration-current/40 hover:opacity-70 transition text-left"
          >
            {match}
          </button>
        );
      } else if (handledAsInternal) {
        const label = normalizeInternalPath(to);
        parts.push(
          <button
            key={`int-${start}`}
            type="button"
            onClick={(e) => go(e, to)}
            onMouseDown={(e) => e.preventDefault()}
            className="underline underline-offset-2 decoration-current/40 hover:opacity-70 transition text-left"
          >
            {label}
          </button>
        );
      } else {
        parts.push(
          <a
            key={`ext-${start}`}
            href={match}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 decoration-current/40 hover:opacity-70 transition"
          >
            {match}
          </a>
        );
      }
    } else {
      parts.push(
        <button
          key={`path-${start}`}
          type="button"
          onClick={(e) => go(e, match)}
          onMouseDown={(e) => e.preventDefault()}
          className="underline underline-offset-2 decoration-current/40 hover:opacity-70 transition text-left"
        >
          {normalizeInternalPath(match)}
        </button>
      );
    }

    last = end;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** ===== Burbuja bienvenida (sin imagen) ===== */
function WelcomeBubble({ visible, onClose, onOpenChat, reduceMotion }) {
  if (!visible) return null;
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.98 }}
      transition={
        reduceMotion
          ? { duration: 0.2 }
          : { type: "spring", stiffness: 350, damping: 26 }
      }
      style={{
        position: "fixed",
        right: "calc(env(safe-area-inset-right, 0px) + 1rem)",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 5.5rem)",
        zIndex: 125,
      }}
      className="group hidden w-[min(20rem,calc(100vw-2rem))] rounded-[0.9375rem] border border-[#697B68] bg-[#F8F2EA] text-[#131613] shadow-[0_18px_42px_-24px_rgba(19,22,19,0.55)] px-4 py-3 relative lg:block"
    >
      <span className="pointer-events-none absolute -bottom-2 right-6 h-4 w-4 rotate-45 border-b border-r border-[#697B68] bg-[#F8F2EA]" />
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight">Hola! ¿Cómo estás?</p>
        <p className="text-xs text-[#697B68] mt-0.5">Soy el bot de Sebas. Decime que necesitas y te ayudo.</p>
        <div className="mt-2 flex gap-2">
          <button
            onClick={onOpenChat}
            className="text-xs px-3 py-1.5 rounded-[10px] bg-[#697B68] text-[#F8F2EA] hover:bg-[#5c6e5b] transition-colors"
          >
            Abrir chat
          </button>
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-[10px] border border-[#697B68]/40 text-[#697B68] hover:border-[#E09767] hover:text-[#131613] transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function Chatbot({
  fabIcon,
  fabAltOpen = "Abrir chat",
  fabAltClose = "Cerrar chat",
}) {
  const { open, setOpen } = useContext(ContextGeneral);
  const navigate = useNavigate();

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hola, soy el bot de Sebas. ¿Cómo puedo ayudarte?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(false);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const isMobile = useIsMobile();

  // Animaciones-lite: móvil o prefers-reduced-motion
  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined") return true;
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const isSmall = isMobile;
    return prefersReduced || isSmall;
  }, [isMobile]);

  /** Autoscroll */
  useEffect(() => {
    // rAF para evitar saltos
    const id = requestAnimationFrame(() => {
      if (!messages.some((message) => message.role === "user")) {
        if (bottomRef.current?.parentElement) {
          bottomRef.current.parentElement.scrollTop = 0;
        }
        return;
      }
      bottomRef.current?.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "end",
      });
    });
    return () => cancelAnimationFrame(id);
  }, [messages, open, loading, reduceMotion]);

  /** Autosize */
  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    el.style.height = Math.min(el.scrollHeight, rootSize * 6, window.innerHeight * 0.25) + "px";
  };
  useEffect(() => autoResize(), [input, open]);

  /** Enter: enviar */
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /** El backend vuelve a validar y recortar este historial. */
  const buildHistory = () =>
    messages.slice(-6).map((m) => ({
      role: m.role === "user" ? "user" : "assistant",
      content: m.text,
    }));

  async function sendMessage(e) {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q) return;

    if (q.length > MAX_INPUT_CHARS) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "El mensaje no puede superar " + MAX_INPUT_CHARS + " caracteres.",
        },
      ]);
      return;
    }

    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");

    setLoading(true);
    try {
      const response = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: q,
          history: buildHistory(),
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || "No pude consultar al asistente.");
      }

      const out = data?.reply || "No pude generar respuesta.";

      // Limpieza: etiquetas y puntuación pegada a URLs
      const cleaned = removeTerminalDotAfterUrls(
        stripSectionLabels(out.trim())
      );
      setMessages((m) => [...m, { role: "assistant", text: cleaned }]);
    } catch (error) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            error?.message ||
            "Hubo un error consultando al asistente. Proba nuevamente en unos minutos.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  // Posiciones
  const fabPos = {
    right: "calc(env(safe-area-inset-right, 0px) + 1rem)",
    bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
  };
  const panelPos = {
    right: "calc(env(safe-area-inset-right, 0px) + 1rem)",
    bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem + 4.5rem)",
  };

  /** Lock scroll mobile */
  useEffect(() => {
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open, isMobile]);

  /** Burbuja bienvenida */
  useEffect(() => {
    const t1 = setTimeout(() => setBubbleVisible(true), 1600);
    const t2 = setTimeout(() => setBubbleVisible(false), 8200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <>
      <Portal>
        {/* Backdrop mobile (menos blur en mobile para menor costo de pintura) */}
        <AnimatePresence>
          {open && isMobile && (
            <motion.div
              key="chatbot-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[120]"
              style={{
                background: "rgba(19, 22, 19, 0.46)",
                backdropFilter: isMobile ? "none" : "blur(8px)",
                WebkitBackdropFilter: isMobile ? "none" : "blur(8px)",
              }}
              onClick={() => setOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Burbuja bienvenida */}
        <AnimatePresence>
          {!open && (
            <WelcomeBubble
              visible={bubbleVisible}
              onClose={() => setBubbleVisible(false)}
              onOpenChat={() => {
                setBubbleVisible(false);
                setOpen(true);
              }}
              reduceMotion={reduceMotion}
            />
          )}
        </AnimatePresence>

        {/* FAB */}
        <motion.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? fabAltClose : fabAltOpen}
          title={open ? fabAltClose : fabAltOpen}
          style={{ position: "fixed", ...fabPos, zIndex: 130 }}
          whileHover={reduceMotion ? undefined : { scale: 1.06, rotate: 3 }}
          whileTap={{ scale: 0.92 }}
          className={[
            "h-14 w-14 rounded-full border-2 border-[#EADFD2]",
            "shadow-[0_16px_34px_-16px_rgba(19,22,19,0.65)]",
            "bg-[#697B68] hover:bg-[#5c6e5b]",
            "text-[#F8F2EA] flex items-center justify-center",
            "transition-all duration-300",
            "focus:outline-none focus-visible:ring-4 focus-visible:ring-[#E09767]/45",
            "relative overflow-hidden",
          ].join(" ")}
        >
          {!open && fabIcon && (
            <span className="absolute inset-1 rounded-full overflow-hidden ring-2 ring-white/20">
              <img
                src={fabIcon}
                alt="Abrir chat"
                width={56}
                height={56}
                decoding="async"
                loading="lazy"
                className="h-full w-full object-cover select-none"
                draggable="false"
              />
            </span>
          )}
          {!open && !fabIcon && (
            <svg
              className="w-7 h-7 relative"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          )}
          {open && (
            <svg
              className="w-7 h-7 relative"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          )}
        </motion.button>

        {/* Panel */}
        <AnimatePresence>
          {open && (
            <motion.aside
              key="chatbot-panel"
              role="dialog"
              aria-label="Chatbot de Sebas"
              style={{
                ...panelPos,
                zIndex: 125,
                contentVisibility: "auto",
                containIntrinsicSize: "352px 608px",
              }}
              initial={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: 20, scale: 0.95 }
              }
              animate={
                reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }
              }
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: 20, scale: 0.95 }
              }
              transition={
                reduceMotion
                  ? { duration: 0.2 }
                  : { type: "spring", stiffness: 300, damping: 30 }
              }
              className={[
                "fixed",
                "w-[min(28rem,calc(100vw-2rem))] max-h-[calc(100dvh-7rem)] h-[38rem]",
                "rounded-[0.9375rem] overflow-hidden",
                "bg-[#F8F2EA] border border-[#697B68]/50",
                "shadow-[0_24px_55px_-24px_rgba(19,22,19,0.58)]",
                "flex flex-col text-[#131613]",
              ].join(" ")}
            >
              {/* Header */}
              <header className="relative flex shrink-0 items-center justify-between gap-3 px-5 py-4 border-b border-[#131613]/10 bg-[#697B68] text-[#F8F2EA]">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <motion.span
                      className="h-3 w-3 rounded-full bg-[#E09767]"
                      animate={
                        reduceMotion ? undefined : { scale: [1, 1.2, 1] }
                      }
                      transition={
                        reduceMotion
                          ? undefined
                          : { duration: 2, repeat: Infinity }
                      }
                    />
                    {!reduceMotion && (
                      <span className="absolute inset-0 rounded-full bg-[#EFB78D] animate-ping opacity-40" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#F8F2EA]">
                      Asistente IA
                    </h3>
                    <p className="text-xs text-[#EADFD2]">En linea</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar conversacion"
                  title="Cerrar"
                  className="h-9 w-9 rounded-[10px] grid place-items-center hover:bg-[#EADFD2]/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EFB78D]"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </header>

              {/* Chat */}
              <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4 text-base scrollbar-thin scrollbar-thumb-[#697B68]/50 scrollbar-track-transparent will-change-transform bg-[#F8F2EA]">
                <AnimatePresence mode="popLayout">
                  {messages.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, y: 10, scale: 0.95 }
                      }
                      animate={
                        reduceMotion
                          ? { opacity: 1 }
                          : { opacity: 1, y: 0, scale: 1 }
                      }
                      exit={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, scale: 0.95 }
                      }
                      transition={
                        reduceMotion
                          ? { duration: 0.15 }
                          : { type: "spring", stiffness: 400, damping: 25 }
                      }
                      className={[
                        "flex",
                        m.role === "user" ? "justify-end" : "justify-start",
                      ].join(" ")}
                    >
                      <div
                        className={[
                          "max-w-[85%] rounded-[0.9375rem] px-4 py-3 break-words leading-relaxed whitespace-pre-line",
                          m.role === "user"
                            ? "bg-[#697B68] text-[#F8F2EA] rounded-br-[4px]"
                            : "bg-[#EADFD2] text-[#131613] border border-[#697B68]/25 rounded-bl-[4px]",
                        ].join(" ")}
                      >
                        {renderWithLinks(m.text, navigate)}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="max-w-[85%] rounded-[0.9375rem] rounded-bl-[4px] px-4 py-3 bg-[#EADFD2] border border-[#697B68]/25">
                      <div className="flex items-center gap-1.5">
                        {[0, 1, 2].map((i) => (
                          <motion.span
                            key={i}
                            className="h-2 w-2 rounded-full bg-[#E09767]"
                            animate={
                              reduceMotion ? undefined : { y: [0, -8, 0] }
                            }
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: i * 0.15,
                              ease: "easeInOut",
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {!messages.some((m) => m.role === "user") && !loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-wrap gap-2 pt-2"
                  >
                    {SUGGESTIONS.map((s, idx) => (
                      <motion.button
                        key={s}
                        initial={
                          reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10 }
                        }
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: reduceMotion ? 0 : 0.2 + idx * 0.08,
                        }}
                        whileHover={
                          reduceMotion ? undefined : { scale: 1.05, y: -2 }
                        }
                        whileTap={reduceMotion ? undefined : { scale: 0.95 }}
                        className="text-sm px-3 py-2 rounded-[10px] border border-[#697B68]/45 bg-transparent text-[#697B68] hover:border-[#E09767] hover:bg-[#EADFD2]/55 hover:text-[#131613] transition-colors"
                        onClick={() => setInput(s)}
                      >
                        {s}
                      </motion.button>
                    ))}
                  </motion.div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <form
                onSubmit={sendMessage}
                className="shrink-0 p-4 border-t border-[#697B68]/25 flex flex-col gap-2 bg-[#EADFD2]"
              >
                <div className="flex items-end gap-3">
                  <div className="min-w-0 flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    placeholder="Escribe tu mensaje..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onInput={autoResize}
                    onKeyDown={onKeyDown}
                    disabled={loading}
                    maxLength={MAX_INPUT_CHARS}
                    className="w-full resize-none rounded-[10px] px-4 py-3 pr-14 text-base bg-[#F8F2EA] text-[#131613] placeholder:text-[#697B68]/70 border border-[#697B68]/45 outline-none focus:border-[#E09767] focus:ring-2 focus:ring-[#E09767]/25 disabled:opacity-60 transition-colors"
                  />
                  {input.trim() && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <div className="text-xs text-[#697B68] bg-[#EADFD2] px-1.5 py-0.5 rounded-[6px]">
                        {input.length}/{MAX_INPUT_CHARS}
                      </div>
                    </motion.div>
                  )}
                </div>
                <motion.button
                  type="submit"
                  disabled={loading || !input.trim()}
                  whileHover={reduceMotion ? undefined : { scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="shrink-0 h-12 w-12 rounded-[10px] text-sm font-semibold bg-[#E09767] text-[#131613] border border-[#131613]/10 disabled:opacity-45 disabled:cursor-not-allowed transition-colors hover:bg-[#EFB78D] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#697B68] flex items-center justify-center group"
                >
                  {loading ? (
                    <motion.div
                      animate={reduceMotion ? undefined : { rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </motion.div>
                  ) : (
                    <svg
                      className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  )}
                </motion.button>
                </div>
              </form>

              {/* Footer */}
              <footer className="flex shrink-0 items-center justify-between px-5 py-3 text-xs text-[#697B68] bg-[#EADFD2] border-t border-[#697B68]/15">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-[#697B68]" />
                  <span className="font-medium">Limites del servidor activos</span>
                </div>
              </footer>
            </motion.aside>
          )}
        </AnimatePresence>
      </Portal>
    </>
  );
}

export default Chatbot;
